
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const navigate = useNavigate();

  // Search data for all pages
  const searchData = [
    // Home page content
    { title: 'Home', path: '/', content: 'Canadian Pharmacy Veterinary Association CPVA healthcare professionals membership programs' },
    { title: 'Join CPVA Today', path: '/membership', content: 'membership benefits pharmacists veterinarians join today' },
    
    // About page content
    { title: 'About CPVA', path: '/about', content: 'about mission history leadership values impact Canadian pharmacy veterinary association' },
    { title: 'Our Mission', path: '/about', content: 'mission statement healthcare excellence pharmacy veterinary medicine' },
    { title: 'Leadership Team', path: '/about', content: 'leadership team board directors executives management' },
    
    // Membership page content
    { title: 'Membership', path: '/membership', content: 'membership types benefits professional student associate application' },
    { title: 'Professional Membership', path: '/membership', content: 'professional membership pharmacists veterinarians benefits continuing education' },
    { title: 'Student Membership', path: '/membership', content: 'student membership pharmacy veterinary students discounted rates' },
    
    // Programs page content
    { title: 'Programs', path: '/programs', content: 'programs education wellness academy advocacy community events training' },
    { title: 'Education Hub', path: '/programs', content: 'education hub continuing education courses certification training' },
    { title: 'Wellness Academy', path: '/programs', content: 'wellness academy mental health support work life balance' },
    
    // Advocacy page content
    { title: 'Advocacy', path: '/advocacy', content: 'advocacy policy initiatives legislative wins campaigns government relations' },
    { title: 'Policy Initiatives', path: '/advocacy', content: 'policy initiatives prescribing authority telehealth one health framework' },
    { title: 'Legislative Wins', path: '/advocacy', content: 'legislative wins pharmacist immunization authority veterinary emergency care' },
    
    // News page content
    { title: 'News', path: '/news', content: 'news updates articles policy education community research events breaking news' },
    { title: 'Featured News', path: '/news', content: 'featured news healthcare investment federal funding policy updates' },
    { title: 'Newsletter', path: '/news', content: 'newsletter subscription updates policy alerts breaking news' },

    // Students page content
    { title: 'Students', path: '/students', content: 'students pharmacy veterinary student membership scholarships events council' },
    { title: 'Student Membership', path: '/students', content: 'student membership benefits resources scholarships events networking' },
    { title: 'Student Council', path: '/students', content: 'student council representatives elections leadership opportunities' },
    { title: 'Scholarships', path: '/students', content: 'scholarships awards financial assistance student funding excellence' },
    { title: 'Student Events', path: '/students', content: 'student events conferences workshops career fairs networking' }
  ];

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  };

  const getSearchResults = () => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    return searchData.filter(item => 
      item.title.toLowerCase().includes(query) || 
      item.content.toLowerCase().includes(query)
    ).slice(0, 5); // Limit to 5 results
  };

  const handleResultClick = (path: string) => {
    setShowSearchResults(false);
    setSearchQuery('');
    navigate(path);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const results = getSearchResults();
    if (results.length > 0) {
      handleResultClick(results[0].path);
    }
  };

  return (
    <header className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <div className="text-2xl font-bold text-blue-900" style={{ fontFamily: '"Pacifico", serif' }}>
                CPVA
              </div>
            </Link>
          </div>
          
          <div className="hidden md:flex items-center space-x-8">
            <nav className="flex space-x-8">
              <Link to="/about" className="text-gray-700 hover:text-blue-900 font-medium">About CPVA</Link>
              <Link to="/membership" className="text-gray-700 hover:text-blue-900 font-medium">Membership</Link>
              <Link to="/programs" className="text-gray-700 hover:text-blue-900 font-medium">Programs</Link>
              <Link to="/advocacy" className="text-gray-700 hover:text-blue-900 font-medium">Advocacy</Link>
              <Link to="/news" className="text-gray-700 hover:text-blue-900 font-medium">News</Link>
              <Link to="/students" className="text-gray-700 hover:text-blue-900 font-medium">Students</Link>
            </nav>
            
            <div className="flex items-center space-x-4">
              <div className="relative">
                <form onSubmit={handleSearchSubmit}>
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-64 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button type="submit" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-600 cursor-pointer">
                    <i className="ri-search-line"></i>
                  </button>
                </form>
                
                {showSearchResults && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                    {getSearchResults().length > 0 ? (
                      <div className="py-2">
                        {getSearchResults().map((result, index) => (
                          <button
                            key={index}
                            onClick={() => handleResultClick(result.path)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{result.title}</div>
                            <div className="text-sm text-gray-600 truncate">{result.content}</div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-3 text-gray-500 text-sm">
                        No results found for "{searchQuery}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Link to="/login" className="text-blue-900 hover:text-blue-700 font-medium">Login</Link>
              <span className="text-gray-400">/</span>
              <Link to="/register" className="text-blue-900 hover:text-blue-700 font-medium">Register</Link>
            </div>
          </div>
          
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-blue-900"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>
        
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              <Link to="/about" className="text-gray-700 hover:text-blue-900 font-medium">About CPVA</Link>
              <Link to="/membership" className="text-gray-700 hover:text-blue-900 font-medium">Membership</Link>
              <Link to="/programs" className="text-gray-700 hover:text-blue-900 font-medium">Programs</Link>
              <Link to="/advocacy" className="text-gray-700 hover:text-blue-900 font-medium">Advocacy</Link>
              <Link to="/news" className="text-gray-700 hover:text-blue-900 font-medium">News</Link>
              <Link to="/students" className="text-gray-700 hover:text-blue-900 font-medium">Students</Link>
              <div className="pt-4 border-t border-gray-200">
                <div className="mb-4">
                  <form onSubmit={handleSearchSubmit}>
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </form>
                </div>
                <div className="flex items-center space-x-4">
                  <Link to="/login" className="text-blue-900 hover:text-blue-700 font-medium">Login</Link>
                  <span className="text-gray-400">/</span>
                  <Link to="/register" className="text-blue-900 hover:text-blue-700 font-medium">Register</Link>
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
